<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useFiltersStore, useWorkspaceStore } from "@/store";
import "@maersk-global/mds-components-core/mc-modal";
import "@maersk-global/mds-components-core/mc-button";
import "@maersk-global/mds-components-core/mc-input";
import "@maersk-global/mds-components-core/mc-error";
import { getRouter } from "@/router";
import { isShipmentsRouteName, isWorkspaceRouteName, ROUTE_NAMES } from "@/router/constants";
import { storeToRefs } from "pinia";
import { useZonePrefix } from "@/features/filters";
import { useSaveWorkspaceWatcher } from "../compositions/useSaveWorkspaceWatcher";
import { useSaveWorkspace } from "../compositions/useSaveWorkspace";

const router = getRouter();
const { t } = useI18n();

const props = defineProps({
  workspaceType: {
    type: String,
    required: true
  }
});

const formModal = ref(false);
const workspaceName = ref("");
const isDuplicateWorkspaceName = ref(false);
const showSaveWorkspaceButton = ref(false);
const workspaceSaveError = ref("");
const workspaceStore = useWorkspaceStore();
const { workspaceTable } = storeToRefs(workspaceStore);

const filtersStore = useFiltersStore();
const { appliedFilters } = storeToRefs(filtersStore);
const { workspaceErrorMessage } = storeToRefs(workspaceStore);
const { setAppliedFilters, getAppliedFiltersAsPayload } = useFiltersStore() || {};

const toggleSaveWorkspaceModal = () => {
  formModal.value = !formModal.value;
};

const currentRouteName = computed(() => {
  return (router?.currentRoute?.value?.name as string)?.toLowerCase();
});

const currentRouteQuery = computed(() => {
  return router?.currentRoute?.value?.query ?? {};
});

const isWorkspaceView = computed(() => isWorkspaceRouteName(currentRouteName.value));

const handleWorkspaceNameChange = (e) => {
  workspaceName.value = e.target.value;

  isDuplicateWorkspaceName.value = isDuplicate(workspaceName.value);
};

const saveWorkspace = async () => {
  if (!workspaceName.value) return;
  const saved = await save(workspaceName.value);
  workspaceSaveError.value = saveError.value;
  if (saved) {
    setAppliedFilters([]);
    workspaceName.value = "";
    formModal.value = false;
  }
};

const getFilterRecipe = computed(() => {
  if (currentRouteName.value === "dnd") {
    if (router?.currentRoute?.value?.query?.view === "map") {
      return "MAP";
    } else {
      return (router?.currentRoute?.value?.query?.direction as string) ?? "IMPORT";
    }
  }
  return currentRouteQuery.value?.tab as string;
});
// Filter zones are registered under the standalone route names, so the embedded
// Track & Trace routes have to map back to them.
const filterZoneRouteName = computed(() => {
  if (isShipmentsRouteName(currentRouteName.value)) return ROUTE_NAMES.SHIPMENTS.toLowerCase();
  if (isWorkspaceRouteName(currentRouteName.value)) return ROUTE_NAMES.WORKSPACE.toLowerCase();
  return currentRouteName.value;
});

const activeAppliedFilter = computed(() => {
  const zonePrefix = useZonePrefix(filterZoneRouteName.value, getFilterRecipe.value);
  return getAppliedFiltersAsPayload(zonePrefix);
});

const activeUserColumns = computed(() => {
  return workspaceTable?.value?.userColumns;
});

const {
  save,
  isDuplicate,
  error: saveError
} = useSaveWorkspace(props.workspaceType, activeAppliedFilter, {
  extraData: () =>
    isShipmentsRouteName(currentRouteName.value) && currentRouteQuery.value?.tab === "active"
      ? [{ type: "user-columns", id: "user-columns", value: JSON.stringify(workspaceTable.value.userColumns) }]
      : []
});

useSaveWorkspaceWatcher(activeAppliedFilter, activeUserColumns, appliedFilters, showSaveWorkspaceButton);
</script>

<template>
  <mc-button
    v-if="showSaveWorkspaceButton && !isWorkspaceView"
    data-test="workspace/save-workspace-button"
    :label="t('SAVE_AS_WORKSPACE')"
    fit="small"
    variant="plain"
    appearance="secondary"
    @click="toggleSaveWorkspaceModal"
  />
  <mc-modal
    data-test="workspace/save-workspace-modal"
    :open="formModal"
    fit="small"
    dimension="small"
    :heading="t('SAVE_AS')"
    @closed.prevent.stop="toggleSaveWorkspaceModal"
  >
    <div>
      <span class="save-workspace-content">{{ t("PLEASE_WRITE_A_NAME_FOR_YOUR_WORKSPACE") }}</span>
      <mc-input
        data-test="workspace/save-workspace-modal-input"
        :value="workspaceName"
        hiddenlabel
        fit="small"
        :placeholder="t('NAME_OF_WORKSPACE')"
        :invalid="isDuplicateWorkspaceName"
        :hint="t('SEARCH_TEXT_NOT_SAVED_AS_FILTERS')"
        :errormessage="t('THIS_WORKSPACE_NAME_ALREADY_EXISTS')"
        @input="handleWorkspaceNameChange"
      ></mc-input>
    </div>
    <mc-button
      slot="secondaryAction"
      data-test="workspace/save-workspace-modal-cancel-button"
      fit="small"
      :label="t('CANCEL')"
      appearance="neutral"
      @click="toggleSaveWorkspaceModal"
    ></mc-button>
    <mc-button
      slot="primaryAction"
      data-test="workspace/save-workspace-modal-save-button"
      :loading="workspaceStore.fetching"
      fit="small"
      :disabled="!workspaceName || isDuplicateWorkspaceName"
      :label="t('SAVE_AS_WORKSPACE')"
      appearance="primary"
      @click="saveWorkspace"
    ></mc-button>
    <mc-error
      v-if="workspaceErrorMessage || workspaceSaveError"
      invalid
      :errormessage="workspaceErrorMessage || workspaceSaveError"
    />
  </mc-modal>
</template>

<style scoped lang="scss">
.save-workspace-content {
  font-size: 14px;
  margin-bottom: 12px;
  color: var(--mds_brand_appearance_neutral_weak_text-color);
}
</style>
